import { DataUtilities } from "../Common/DataUtilities";
import { RuleExecutable } from "../Common/Execution/RuleExecutable";
import { ExecutionData } from "../Common/Data/ExecutionData/ExecutionData";
import { IDataDefinition } from "../Common/Data/Definitions/IDataDefinition";
import { ITestingContext } from "../Common/Testing/ITestingContext";
import { IDesignValidationProvider, ValidationIssue } from "../Common/IDesignValidationProvider";
import { EventLog, EventLogType } from "../Common/Event/EventLog";
import { ExpressionValue } from "./ExpressionValue";
import { ExpressionTestResult } from "./ExpressionTestResult";
import { ExpressionContext } from "./ExpressionContext";
import { RootExpression } from "./RootExpression";
import { IBaseExpressionElement } from "./IBaseExpressionElement";
import { ExpressionRule } from "./ExpressionRule";
import { ExpressionValueType } from "./ExpressionValueType";
import { ExpressionConstants } from "./ExpressionContants";
import type { AppGlueAny } from "../Common/AppGlueAny";

export class ExpressionConfiguration
    extends RuleExecutable
    implements IBaseExpressionElement, IDesignValidationProvider {
    readonly canSelect: boolean = true;

    _id: string = DataUtilities.generateUniqueId();
    editContext: ExpressionContext;
    requiredVariable: IDataDefinition | undefined;
    allowTrueValueResult: boolean = false;
    allowRootUnset: boolean = false;

    constructor() {
        super();
        this.editContext = new ExpressionContext(this);
        this._rootExpression.setExpressionContext(this.editContext, this);

        this._rootExpression.expressionRoot.expectedType = this.getRuleResult();

        this.editContext.definition = this;
    }

    private _rootExpression: RootExpression = new RootExpression();

    get rootExpression(): RootExpression {
        return this._rootExpression;
    }

    get executionResult(): IDataDefinition | (() => IDataDefinition) {
        return this._executionResult!;
    }

    set executionResult(value: IDataDefinition | (() => IDataDefinition)) {
        this._executionResult = value;

        this._rootExpression.expressionValueType = this.getRuleResult();
        this._rootExpression.expressionRoot.expectedType = this.getRuleResult();

        if (this.expression?.__type === ExpressionConstants.IfThenExpressionType) {
            this.expression.expressionValueType = this.getRuleResult();
        }

        if (this.editContext) this.editContext.refresh();
    }

    get expression(): ExpressionRule | undefined {
        return this._rootExpression.expressionRoot.subExpression;
    }

    set expression(value: ExpressionRule | undefined) {
        this.valueType = ExpressionValueType.SUBEXPRESSION;
        this._rootExpression.expressionRoot.subExpression = value;

        if (this.editContext) this.editContext.refresh();
    }

    get variableName(): string | undefined {
        return this._rootExpression.expressionRoot.variableName;
    }

    set variableName(value: string | undefined) {
        this.valueType = ExpressionValueType.VARIABLE;
        this._rootExpression.expressionRoot.variableName = value;

        if (this.editContext) this.editContext.refresh();
    }

    get value(): AppGlueAny {
        return this._rootExpression.expressionRoot.value;
    }

    set value(value: AppGlueAny) {
        this.valueType = ExpressionValueType.VALUE;
        this._rootExpression.expressionRoot.value = value;

        if (this.editContext) this.editContext.refresh();
    }

    get valueType(): ExpressionValueType {
        return this._rootExpression.expressionRoot.valueType;
    }

    set valueType(value: ExpressionValueType) {
        this._rootExpression.expressionRoot.valueType = value;

        if (this.editContext) this.editContext.refresh();
    }

    get expressionRoot() {
        return this._rootExpression.expressionRoot;
    }

    get isEmpty(): boolean {
        return !(
            (this.valueType === ExpressionValueType.SUBEXPRESSION && this.expression !== undefined) ||
            (this.valueType === ExpressionValueType.VALUE && this.value !== undefined) ||
            (this.valueType === ExpressionValueType.VARIABLE && this.variableName !== undefined)
        );
    }

    run(data: ExecutionData, testContext?: ITestingContext): AppGlueAny {
        // create test context for expression.
        let subContext: ExpressionTestResult | undefined;
        if (testContext) {
            subContext = new ExpressionTestResult(this._id, undefined, this);
            testContext.addTestResultItem(subContext);
            subContext.addLogItem(new EventLog("ExpressionDefinition: start evaluating"));
        }
        try {
            let res = this._rootExpression.evaluate(data, subContext);

            if (subContext) {
                subContext.result = res !== undefined ? res.toString() : "undefined";
                subContext.addLogItem(new EventLog(`ExpressionDefinition: end evaluating with result [${res}]`));
            }

            return res;
        } catch (e) {
            if (subContext) {
                subContext.addLogItem(new EventLog(e.message, EventLogType.ERROR));
                subContext.isError = true;
                subContext.result = e.message;
            }
            throw e;
        }
    }

    getAllVariables(): string[] {
        let allVars = this._rootExpression.expressionRoot.getAllVariables();

        // remove dups

        return allVars;
    }

    // eslint-disable-next-line
    setExpressionContext(selection: ExpressionContext, _owner: IBaseExpressionElement): void {
        this.editContext = selection;
        this._rootExpression.setExpressionContext(this.editContext, this);
    }

    getStorageData(): object {
        let data = super.getStorageData();

        Reflect.set(data, "_id", this._id);
        Reflect.set(data, "allowRootUnset", this.allowRootUnset);
        Reflect.set(data, "rootExpression", this._rootExpression.getStorageData());

        return data;
    }

    setStorageData(data: object) {
        super.setStorageData(data);

        this._id = Reflect.get(data, "_id");
        this.allowRootUnset = Reflect.get(data, "allowRootUnset");

        this._rootExpression.setStorageData(Reflect.get(data, "rootExpression"));
    }

    getAllExpressionValues(): ExpressionValue[] {
        return this._rootExpression.getAllExpressionValues();
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        let find: boolean = this.requiredVariable ? false : true;

        if (this.allowRootUnset && this.valueType === ExpressionValueType.UNSET) return [];

        for (let v of this.getAllExpressionValues()) {
            issues.push(...v.getValidationIssues());
            if (v.valueType === ExpressionValueType.VARIABLE && v.variableName === this.requiredVariable?.name) {
                find = true;
            }
        }

        if (
            this.getRuleResult().type !== this._rootExpression.expressionRoot.expectedType.type ||
            this.getRuleResult().list !== this._rootExpression.expressionRoot.expectedType.list
        ) {
            issues.push(new ValidationIssue(`${this.getRuleResult().type} expression not set`));
        }

        if (find) {
            return issues;
        }

        if (
            this.requiredVariable &&
            !(this.valueType === ExpressionValueType.VALUE && this.value === true && this.allowTrueValueResult)
        ) {
            issues.push(new ValidationIssue(`Expression not using required variable: ${this.requiredVariable.name}`));
        }

        return issues;
    }

    getUsedVariables() {
        const variables = this.expression?.getVariableNames();
        return variables || [];
    }

    getVariableName() {
        if (this.variableName) {
            return this.variableName;
        } else {
            const variables = this.expression?.getVariableNames();
            if (variables && variables.length > 0) {
                return variables.slice(-1)[0];
            }
            return null;
        }
    }

    replaceVariables(oldVariablePath: string, newVariablePath: string) {
        for (let v of this.getAllExpressionValues()) {
            if (v.valueType === ExpressionValueType.VARIABLE && v.variableName === oldVariablePath) {
                v.variableName = newVariablePath;
            }
        }
    }
}
